import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Check, MessageCircle, X } from "lucide-react";
import { hostService } from "@/lib/services";
import { extractErrorMessage } from "@/lib/api";
import { formatDate, formatUsh } from "@/lib/format";
import type { Booking, BookingStatus } from "@/types/api";

export const Route = createFileRoute("/_authenticated/host/bookings/")({
  component: HostBookings,
});

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "checked_in", label: "Checked in" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "rejected", label: "Rejected" },
];

const statusStyles: Record<BookingStatus, string> = {
  pending: "bg-accent/40 text-secondary",
  processing: "bg-muted text-ink-muted",
  confirmed: "bg-primary/15 text-primary",
  checked_in: "bg-primary/15 text-primary",
  checked_out: "bg-muted text-ink-muted",
  completed: "bg-muted text-ink-muted",
  cancelled: "bg-destructive/10 text-destructive",
  rejected: "bg-destructive/10 text-destructive",
};

function HostBookings() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>("");
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["host-bookings", { status }],
    queryFn: () => hostService.bookings(status ? { status } : undefined),
  });

  const confirm = useMutation({
    mutationFn: (id: string) => hostService.confirmBooking(id),
    onSuccess: () => {
      toast.success("Booking confirmed.");
      qc.invalidateQueries({ queryKey: ["host-bookings"] });
    },
    onError: (err) => toast.error(extractErrorMessage(err, "Couldn't confirm")),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      hostService.rejectBooking(id, reason || undefined),
    onSuccess: () => {
      toast.success("Booking rejected.");
      setRejectingId(null);
      setReason("");
      qc.invalidateQueries({ queryKey: ["host-bookings"] });
    },
    onError: (err) => toast.error(extractErrorMessage(err, "Couldn't reject")),
  });

  const items = data?.data ?? [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <h1 className="text-3xl font-extrabold tracking-tight text-ink">Bookings</h1>
      <p className="mt-1 text-sm text-ink-muted">
        Review, confirm, and manage incoming reservations.
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            onClick={() => setStatus(f.value)}
            className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition ${
              status === f.value
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-ink-muted hover:bg-muted hover:text-ink"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="mt-6 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-2xl border border-border brand-skeleton"
            />
          ))}
        </div>
      )}

      {isError && (
        <div className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="mt-10 rounded-3xl border border-border bg-card p-10 text-center">
          <p className="text-base font-bold text-ink">No bookings here</p>
          <p className="mt-1 text-sm text-ink-muted">
            When guests book your listings, they'll appear in this list.
          </p>
        </div>
      )}

      <ul className="mt-6 space-y-3">
        {items.map((b: Booking) => {
          const isPending = b.status === "pending";
          const showReject = rejectingId === b.id;
          return (
            <li key={b.id} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wider text-ink-subtle">
                    {b.property?.name ?? "Stay"}
                  </p>
                  <p className="mt-1 text-base font-extrabold text-ink">
                    {formatDate(b.check_in_date)} → {formatDate(b.check_out_date)}
                  </p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {b.guest_count} guest{b.guest_count === 1 ? "" : "s"} · {formatUsh(b.total)}
                  </p>
                  {b.notes && (
                    <p className="mt-2 max-w-xl text-sm text-ink-muted">
                      <span className="font-bold text-ink">Note: </span>
                      {b.notes}
                    </p>
                  )}
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${statusStyles[b.status]}`}
                >
                  {b.status.replace("_", " ")}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Link
                  to="/bookings/$bookingId"
                  params={{ bookingId: b.id }}
                  className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-bold text-ink hover:bg-muted"
                >
                  View details
                </Link>
                <Link
                  to="/chats/$bookingId"
                  params={{ bookingId: b.id }}
                  className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-bold text-ink hover:bg-muted"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> Message guest
                </Link>
                {isPending && !showReject && (
                  <>
                    <button
                      onClick={() => confirm.mutate(b.id)}
                      disabled={confirm.isPending}
                      className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60"
                    >
                      <Check className="h-3.5 w-3.5" /> Confirm
                    </button>
                    <button
                      onClick={() => {
                        setRejectingId(b.id);
                        setReason("");
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-destructive/40 px-3 py-1.5 text-xs font-bold text-destructive hover:bg-destructive/5"
                    >
                      <X className="h-3.5 w-3.5" /> Reject
                    </button>
                  </>
                )}
              </div>

              {showReject && (
                <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/5 p-3">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-destructive">
                    Reason (optional)
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={255}
                    placeholder="Let the guest know why."
                    className="mt-1 block h-10 w-full rounded-lg border border-input bg-background px-3 text-sm text-ink outline-none focus:border-destructive"
                  />
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setRejectingId(null);
                        setReason("");
                      }}
                      className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-bold text-ink hover:bg-muted"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => reject.mutate({ id: b.id, reason })}
                      disabled={reject.isPending}
                      className="rounded-lg bg-destructive px-3 py-1.5 text-xs font-bold text-destructive-foreground hover:opacity-90 disabled:opacity-60"
                    >
                      {reject.isPending ? "Rejecting…" : "Confirm reject"}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
